import React, { useRef, useState, useEffect } from "react";
import { LogOut, Layers, Maximize2, Minimize2, Rows, Activity, Building2, Users, Box, Settings, Sparkles, HelpCircle, UserPlus, History, ListTodo, ChevronDown, CalendarDays, FolderOpen } from "lucide-react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";

import ExportMenu from "./ExportMenu";
import ProjectSwitcher from "./ProjectSwitcher";
import AiQuotaChip from "./AiQuotaChip";

export default function TopBar({ project, projects, currentProjectId, onSelectProject, onNewProject, drift, density, onDensity, onLogout, user, onJumpNow, onOpenAI, onOpenDailyWrap, onOpenHelp, onOpenSettings, onOpenShare, onOpenAudit, myRole, onOpenPanel, groupMode = "date", onGroupMode }) {
  const navigate = useNavigate();
  const [viewOpen, setViewOpen] = useState(false);
  const viewRef = useRef(null);

  // Close view dropdown on outside click / Escape.
  useEffect(() => {
    if (!viewOpen) return;
    const handler = (e) => {
      if (viewRef.current && !viewRef.current.contains(e.target)) setViewOpen(false);
    };
    const esc = (e) => { if (e.key === "Escape") setViewOpen(false); };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", esc);
    };
  }, [viewOpen]);

  const viewLabel = groupMode === "room" ? "ROOMS" : "SCHEDULE";
  const viewIcon = groupMode === "room"
    ? <Building2 className="w-3 h-3" />
    : <CalendarDays className="w-3 h-3" />;
  return (
    <div
      className="h-12 bg-bg-base border-b border-line-strong flex items-center px-3 gap-2 justify-between sticky top-0 z-40"
      data-testid="topbar"
    >
      {/* Left: brand + project */}
      <div className="flex items-center gap-2 lg:gap-4 min-w-0 shrink">
        {/* Brand: hidden on small screens to save space */}
        <div className="hidden md:flex items-center gap-2 mono text-2xs uppercase tracking-[0.2em] text-ink-mute shrink-0">
          <div className="w-2.5 h-2.5 bg-amber" />
          <span className="hidden xl:inline">RIVERSIDE / CONSOLE</span>
          <span className="xl:hidden">CONSOLE</span>
        </div>
        <div className="hidden md:block h-5 w-px bg-line shrink-0" />
        <div className="flex items-center gap-2 min-w-0">
          <span className="hidden xl:inline mono text-2xs text-ink-mute uppercase tracking-widest shrink-0">PROJECT</span>
          <button
            onClick={() => navigate("/projects")}
            className="btn-ghost shrink-0"
            data-testid="topbar-all-projects"
            title="Back to project list"
          >
            <FolderOpen className="w-3 h-3" />
          </button>
          <ProjectSwitcher
            projects={projects || []}
            currentProjectId={currentProjectId}
            onSelect={onSelectProject}
            onNewProject={onNewProject}
          />
          <span
            className={clsx(
              "hidden lg:inline mono text-2xs uppercase tracking-wider px-1.5 py-0.5 border shrink-0",
              project?.status === "active"
                ? "text-lime border-lime/40 bg-lime/10"
                : "text-ink-mute border-line"
            )}
          >
            {project?.status || "—"}
          </span>
        </div>
      </div>

      {/* Right: tools */}
      <div className="flex items-center gap-1.5 lg:gap-2 shrink-0">
        {/* Manage buttons */}
        <div className="flex items-center gap-px">
          <button onClick={() => onOpenPanel("rooms")} className="btn-ghost px-2" title="Rooms" data-testid="open-rooms-panel">
            <Building2 className="w-3 h-3" />
          </button>
          <button onClick={() => onOpenPanel("crew")} className="btn-ghost px-2" title="Crew" data-testid="open-crew-panel">
            <Users className="w-3 h-3" />
          </button>
          <button onClick={() => onOpenPanel("equipment")} className="btn-ghost px-2" title="Equipment" data-testid="open-equipment-panel">
            <Box className="w-3 h-3" />
          </button>
        </div>

        <div className="hidden md:block h-5 w-px bg-line" />

        {/* Drift indicator */}
        <div className="flex items-center gap-1.5" data-testid="drift-indicator" title={`Project drift: ${drift >= 0 ? "+" : ""}${drift}m`}>
          <span className="hidden xl:inline mono text-2xs uppercase tracking-widest text-ink-mute">DRIFT</span>
          <span
            className={clsx(
              "mono text-sm font-semibold tabular-nums",
              drift > 45 ? "text-red" : drift > 15 ? "text-amber" : drift > 0 ? "text-amber/70" : "text-ink-dim"
            )}
          >
            {drift >= 0 ? "+" : ""}
            {drift}m
          </span>
        </div>

        <div className="hidden md:block h-5 w-px bg-line" />

        {/* AI status */}
        <button
          onClick={onOpenAI}
          className="flex items-center gap-1.5 text-ink-dim hover:text-amber transition-colors px-1"
          data-testid="ai-status-btn"
          title="AI command palette (⌘K)"
        >
          <Activity className="w-3.5 h-3.5" />
          <span className="w-1.5 h-1.5 bg-cyan rounded-full animate-pulse-amber" />
          <span className="hidden lg:inline mono text-2xs uppercase tracking-widest">AI</span>
        </button>
        <AiQuotaChip />

        <div className="hidden md:block h-5 w-px bg-line" />

        {/* Density toggle */}
        <div className="hidden sm:flex items-center gap-px" data-testid="density-toggle">
          {[
            { key: "compact", icon: <Minimize2 className="w-3 h-3" />, label: "C" },
            { key: "standard", icon: <Rows className="w-3 h-3" />, label: "S" },
            { key: "expanded", icon: <Maximize2 className="w-3 h-3" />, label: "E" },
          ].map((m) => (
            <button
              key={m.key}
              onClick={() => onDensity(m.key)}
              className={clsx(
                "btn-ghost px-2 py-1",
                density === m.key ? "active" : ""
              )}
              title={m.key}
              data-testid={`density-${m.key}`}
            >
              {m.icon}
            </button>
          ))}
        </div>

        <button onClick={onJumpNow} className="btn-ghost px-2" data-testid="jump-now-btn" title="Jump to now">
          <Layers className="w-3 h-3" /> <span className="hidden xl:inline">NOW</span>
        </button>

        <button
          onClick={onOpenDailyWrap}
          className="btn-ghost px-2"
          data-testid="daily-wrap-btn"
          title="AI Daily Wrap — narrate this day"
        >
          <Sparkles className="w-3 h-3" /> <span className="hidden xl:inline">WRAP</span>
        </button>

        {/* VIEW dropdown — switch between schedule (date-grouped), rooms
            (room-grouped), and the dedicated /plan view. */}
        <div className="relative" ref={viewRef}>
          <button
            onClick={() => setViewOpen((v) => !v)}
            className="btn-ghost px-2"
            data-testid="view-dropdown-btn"
            title="Switch view"
          >
            {viewIcon}
            <span className="hidden lg:inline ml-1">{viewLabel}</span>
            <ChevronDown className="w-3 h-3 ml-1 opacity-60" />
          </button>
          {viewOpen && (
            <div
              className="absolute right-0 top-full mt-1 bg-bg-surface border border-line-strong shadow-[0_8px_32px_rgba(0,0,0,0.6)] z-50 min-w-[180px]"
              data-testid="view-dropdown-menu"
            >
              <ViewItem
                active={groupMode === "date"}
                icon={<CalendarDays className="w-3 h-3" />}
                label="SCHEDULE"
                sub="Grouped by day"
                onClick={() => { onGroupMode && onGroupMode("date"); setViewOpen(false); }}
                testid="view-item-schedule"
              />
              <ViewItem
                active={groupMode === "room"}
                icon={<Building2 className="w-3 h-3" />}
                label="ROOMS"
                sub="Grouped by room"
                onClick={() => { onGroupMode && onGroupMode("room"); setViewOpen(false); }}
                testid="view-item-rooms"
              />
              <div className="h-px bg-line my-1" />
              <ViewItem
                active={false}
                icon={<ListTodo className="w-3 h-3" />}
                label="PLAN"
                sub="Bulk-dump unscheduled tasks"
                onClick={() => { setViewOpen(false); navigate("/plan"); }}
                testid="view-item-plan"
              />
              <ViewItem
                active={false}
                icon={<Box className="w-3 h-3" />}
                label="EQUIPMENT"
                sub="Assign gear to rooms"
                onClick={() => { setViewOpen(false); navigate("/equipment"); }}
                testid="view-item-equipment"
              />
            </div>
          )}
        </div>

        <ExportMenu projectId={project?.id} />

        <button
          onClick={onOpenShare}
          className="btn-ghost px-2"
          data-testid="share-btn"
          title="Share project · invite members"
        >
          <UserPlus className="w-3 h-3" /> <span className="hidden xl:inline">SHARE</span>
        </button>

        <button
          onClick={onOpenSettings}
          className="btn-ghost px-2"
          data-testid="project-settings-btn"
          title="Project settings"
        >
          <Settings className="w-3 h-3" />
        </button>

        <button
          onClick={onOpenAudit}
          className="btn-ghost px-2"
          data-testid="audit-btn"
          title="Audit log — every change on this project"
        >
          <History className="w-3 h-3" /> <span className="hidden xl:inline">AUDIT</span>
        </button>

        <button
          onClick={onOpenHelp}
          className="btn-ghost px-2"
          data-testid="help-btn"
          title="Help (Shift + ?)"
        >
          <HelpCircle className="w-3 h-3" /> <span className="hidden xl:inline">HELP</span>
        </button>

        <div className="hidden md:block h-5 w-px bg-line" />

        {/* User */}
        <div className="flex items-center gap-2">
          <div className="mono text-2xs text-ink-mute uppercase tracking-widest hidden xl:block max-w-[120px] truncate" title={user?.name || ""}>
            {user?.name || "—"}
          </div>
          <button
            onClick={onLogout}
            className="btn-ghost px-2"
            data-testid="logout-btn"
            title="Sign out"
          >
            <LogOut className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ViewItem({ active, icon, label, sub, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        "w-full text-left px-3 py-2 flex items-center gap-2 transition-colors hover:bg-amber-glow group",
        active && "bg-amber/10"
      )}
      data-testid={testid}
    >
      <span className={clsx("shrink-0", active ? "text-amber" : "text-ink-mute group-hover:text-amber")}>{icon}</span>
      <div className="flex-1 min-w-0">
        <div className={clsx("mono text-2xs uppercase tracking-widest", active ? "text-amber" : "text-ink")}>{label}</div>
        <div className="text-2xs text-ink-mute mt-0.5">{sub}</div>
      </div>
      {active && <span className="w-1.5 h-1.5 bg-amber shrink-0" />}
    </button>
  );
}
